export type LoadGrowth = "linear" | "logarithmic";

export interface LoadEstimateInput {
  meanTimePerStoreMs: number;
  storeCount: number;
  displayedDayCount?: number;
  windowDayGrowthRate?: number;
  growth: LoadGrowth;
  logarithmicLinearWeight?: number;
}

const asPositiveInteger = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? Math.max(1, Math.ceil(value)) : 1;

// Keeps the curve predominantly logarithmic while avoiding an overly flat estimate for broad scopes.
const logarithmicLinearWeight = 0.05;
// The selected window affects query cost, but materially less than additional stores.
const windowDayGrowthRate = 0.01;

const asWeight = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : logarithmicLinearWeight;

const asWindowDayGrowthRate = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? Math.max(0, value) : windowDayGrowthRate;

/** Calculates a request expectation from its calibrated one-store mean. */
export const calculateExpectedLoadTimeMs = ({
  meanTimePerStoreMs,
  storeCount,
  displayedDayCount,
  windowDayGrowthRate: requestedWindowDayGrowthRate,
  growth,
  logarithmicLinearWeight: requestedLinearWeight,
}: LoadEstimateInput): number => {
  const baseTime =
    typeof meanTimePerStoreMs === "number" && Number.isFinite(meanTimePerStoreMs)
      ? Math.max(0, meanTimePerStoreMs)
      : 0;
  const normalizedStoreCount = asPositiveInteger(storeCount);
  const normalizedDayCount = asPositiveInteger(displayedDayCount);
  const linearWeight = asWeight(requestedLinearWeight);
  const dayGrowthRate = asWindowDayGrowthRate(requestedWindowDayGrowthRate);
  const windowMultiplier = Math.exp(dayGrowthRate * (normalizedDayCount - 1));
  const storeMultiplier =
    growth === "logarithmic"
      ? (1 - linearWeight) * Math.log2(normalizedStoreCount + 1) +
        linearWeight * normalizedStoreCount
      : normalizedStoreCount;

  return Math.round(baseTime * storeMultiplier * windowMultiplier);
};
